import type { PersonifyConfig } from '@/services/personify-config';
import { scrubPersonifyReturn } from '@/services/personify-return-scrub';
import { SIGNUP_ENTRY_PATH } from '../steps';

export { readPersonifyConfig, type PersonifyConfig } from '@/services/personify-config';

/**
 * Personify Health's login gate, kept from the legacy signup app: members on
 * Personify's own link sign in to the Personify portal before signup starts,
 * and come back with `authenticated=true`. It is a soft gate. The returned
 * authorization code is never exchanged, so no client secret is ever needed
 * and none is sent: the secret only matters when a code is redeemed.
 */

/** The Pelago client id Personify's members arrive with. */
export const PERSONIFY_PELAGO_CLIENT_ID = '140';

/** The OAuth scopes the legacy gate asked for. */
export const PERSONIFY_SCOPE = 'events offline_access';

export type PersonifyReturnKind = 'authenticated' | 'returned_error';

export type PersonifyGateReason = 'redirect' | PersonifyReturnKind | 'disabled';

export type PersonifyGateDecision =
  | { readonly applies: false }
  | { readonly applies: true; readonly reason: PersonifyGateReason };

function isPersonifyLink(params: URLSearchParams): boolean {
  return params.get('client_id') === PERSONIFY_PELAGO_CLIENT_ID;
}

function returnKindOf(params: URLSearchParams): PersonifyReturnKind | null {
  if (params.has('error') || params.has('error_description')) return 'returned_error';
  if (params.get('authenticated') === 'true') return 'authenticated';
  return null;
}

/**
 * Whether this entry is Personify's, and if so what the gate does with it. A
 * return recorded at landing wins over the URL, because the landing scrub has
 * already taken the OAuth error off it.
 */
export function personifyGateDecision(
  params: URLSearchParams,
  config: PersonifyConfig | null,
  recordedReturn: PersonifyReturnKind | null = takenReturnKind,
): PersonifyGateDecision {
  if (!isPersonifyLink(params)) return { applies: false };
  const returned = recordedReturn ?? returnKindOf(params);
  if (returned) return { applies: true, reason: returned };
  if (!config) return { applies: true, reason: 'disabled' };
  return { applies: true, reason: 'redirect' };
}

/** Where Personify sends the member back to: the signup entry, link intact. */
export function personifyReturnUrl(origin: string, params: URLSearchParams): string {
  const query = new URLSearchParams(params);
  query.set('authenticated', 'true');
  query.set('client_id', PERSONIFY_PELAGO_CLIENT_ID);
  return `${origin}${SIGNUP_ENTRY_PATH}?${query.toString()}`;
}

/** Personify's authorize request. Exactly four params; never a secret. */
export function personifyAuthorizeUrl(
  config: PersonifyConfig,
  origin: string,
  params: URLSearchParams,
): string {
  const url = new URL(config.authorizeUrl);
  url.search = new URLSearchParams({
    client_id: config.clientId,
    scope: PERSONIFY_SCOPE,
    response_type: 'code',
    redirect_uri: personifyReturnUrl(origin, params),
  }).toString();
  return url.toString();
}

let takenReturnKind: PersonifyReturnKind | null = null;

/**
 * The kind of Personify return this page load arrived with. The root layout's
 * `<head>` script has already scrubbed the address bar before Next started
 * (see `scrubPersonifyReturn`); calling it again here is a no-op on a clean URL
 * and covers a load where that script did not run. Only the kind is kept.
 */
export function takePersonifyReturn(): PersonifyReturnKind | null {
  if (typeof window === 'undefined') return null;
  scrubPersonifyReturn();
  const recorded = document.documentElement.getAttribute('data-personify-return');
  if (recorded === 'authenticated' || recorded === 'returned_error') takenReturnKind = recorded;
  return takenReturnKind;
}

/** Test seam: forget the recorded return between cases. */
export function resetPersonifyReturn(): void {
  takenReturnKind = null;
  if (typeof document !== 'undefined')
    document.documentElement.removeAttribute('data-personify-return');
}
